/**
 * Price Gate B2B — ComIreland Trade Theme
 * Hides prices, blocks buy buttons, and redirects cart for non-approved users.
 * Level is injected server-side by sections/price-gate.liquid
 *
 * Post-login redirect:
 * Shopify New Customer Accounts supports return_to natively via the
 * /customer_authentication/login endpoint. No localStorage needed.
 *
 * Back to store:
 * Tracks the last visited store page in localStorage (pg_store_return) so
 * the /pages/back-to-store redirect page can return the user to where they were.
 *
 * Robustness (06-05-2026):
 * Uses a MutationObserver to re-apply the gate when external scripts
 * (e.g. Clay B2B Wholesale Pricing) overwrite .price/.product-form__buttons
 * innerHTML after our initial pass. Observer is only active for non-approved
 * levels and is disconnected during our own writes to avoid loops.
 * See docs/06-05-2026-pricegate-clay-conflict.md
 */
(function () {
  var config = window.comirelandPriceGate || { level: 'guest' };

  // --- Track last visited store page ---
  // Saved on every page except the redirect page itself.
  // Used by /pages/back-to-store to return the user to where they were.
  if (window.location.pathname !== '/pages/back-to-store') {
    localStorage.setItem('pg_store_return', window.location.pathname + window.location.search);
  }

  // Approved customers: remove hiding class and exit — nothing to gate
  if (config.level === 'approved') {
    document.documentElement.classList.remove('price-gate-hide');
    return;
  }

  // --- Cart redirect ---
  // If a non-approved user lands on /cart, redirect to login with return_to.
  // Skip in Shopify theme editor (designMode) to allow customizer to work.
  if (window.location.pathname === '/cart' && !(window.Shopify && window.Shopify.designMode)) {
    window.location.replace('/customer_authentication/login?return_to=' + encodeURIComponent('/cart'));
    return;
  }

  // --- Login URL with native return_to support ---
  var loginUrl = '/customer_authentication/login?return_to=' + encodeURIComponent(window.location.pathname + window.location.search);

  // --- Shared helpers ---
  var ICON_LOCK = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="vertical-align:middle;margin-right:5px;flex-shrink:0"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>';

  var PRICE_MESSAGES = {
    guest:    '<a href="' + loginUrl + '" style="display:inline-flex;align-items:center;text-decoration:none;color:inherit;font-size:0.9em;opacity:0.85;">' + ICON_LOCK + 'Login to see price</a>',
    pending:  '<span style="display:inline-flex;align-items:center;font-size:0.9em;opacity:0.75;">&#x23F1;&nbsp;Account pending approval</span>',
    loggedin: '<a href="/pages/trade-account" style="display:inline-flex;align-items:center;text-decoration:none;color:inherit;font-size:0.9em;opacity:0.85;">' + ICON_LOCK + 'Apply for wholesale access</a>'
  };

  var BUY_MESSAGES = {
    guest:
      '<div style="display:flex;gap:0.75rem;flex-wrap:wrap;align-items:center;">' +
      '<a href="' + loginUrl + '" class="button">' + ICON_LOCK + 'Login to purchase</a>' +
      '<a href="/pages/trade-account" class="button button--secondary">Register Now</a>' +
      '</div>',
    pending:  '<p style="font-size:0.9em;opacity:0.75;margin:0;">&#x23F1;&nbsp;Your account is pending approval. We\'ll notify you once it\'s active.</p>',
    loggedin: '<a href="/pages/trade-account" class="button">' + ICON_LOCK + 'Apply for wholesale access</a>'
  };

  // Marker tokens to detect whether our gate content is still in place.
  // If none of these is found inside the element's textContent, we assume
  // an external script (e.g. Clay) has overwritten it and we re-apply.
  var GATED_MARKERS = [
    'Account pending approval',
    'Login to see price',
    'Apply for wholesale access',
    'Login to purchase',
    'Register Now',
    'pending approval'
  ];

  // --- MutationObserver state ---
  var observer = null;
  var rafId = null;

  function isStillGated(el) {
    var t = el.textContent || '';
    for (var i = 0; i < GATED_MARKERS.length; i++) {
      if (t.indexOf(GATED_MARKERS[i]) !== -1) return true;
    }
    return false;
  }

  // --- A: Price gate ---
  function applyPriceGate() {
    if (observer) observer.disconnect();
    document.querySelectorAll('.price').forEach(function (el) {
      // External script (e.g. Clay) may have overwritten our content while
      // leaving data-pg in place. Detect this and force re-apply.
      if (el.hasAttribute('data-pg') && !isStillGated(el)) {
        el.removeAttribute('data-pg');
      }
      if (el.hasAttribute('data-pg')) return;
      el.setAttribute('data-pg', '1');
      var msg = PRICE_MESSAGES[config.level] || PRICE_MESSAGES.guest;
      el.innerHTML = '<div style="padding:0.35rem 0;">' + msg + '</div>';
      el.style.visibility = '';
    });
    document.documentElement.classList.remove('price-gate-hide');
    if (observer) observer.observe(document.body, { childList: true, subtree: true });
  }

  // --- B: Buy button gate ---
  function applyBuyGate() {
    if (observer) observer.disconnect();
    document.querySelectorAll('.product-form__buttons').forEach(function (el) {
      if (el.hasAttribute('data-bg') && !isStillGated(el)) {
        el.removeAttribute('data-bg');
      }
      if (el.hasAttribute('data-bg')) return;
      el.setAttribute('data-bg', '1');
      var msg = BUY_MESSAGES[config.level] || BUY_MESSAGES.guest;
      el.innerHTML = '<div style="padding:0.5rem 0;">' + msg + '</div>';
    });
    if (observer) observer.observe(document.body, { childList: true, subtree: true });
  }

  function reapply() {
    rafId = null;
    applyPriceGate();
    applyBuyGate();
  }

  function scheduleReapply() {
    if (rafId !== null) return;
    rafId = requestAnimationFrame(reapply);
  }

  function resetAndReapply() {
    document.querySelectorAll('[data-pg]').forEach(function (el) { el.removeAttribute('data-pg'); });
    document.querySelectorAll('[data-bg]').forEach(function (el) { el.removeAttribute('data-bg'); });
    applyPriceGate();
    applyBuyGate();
  }

  // --- Observer setup ---
  // Watches for external scripts (e.g. Clay) that overwrite gated nodes
  // or inject new .price / .product-form__buttons (lazy load, quickview).
  // Mutations are coalesced into a single rAF-batched re-apply.
  function setupObserver() {
    if (typeof MutationObserver === 'undefined') return;
    observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        var t = m.target;
        if (!t || t.nodeType !== 1) continue;

        // Mutation inside or directly on a relevant element
        var classList = t.classList;
        if ((classList && (classList.contains('price') || classList.contains('product-form__buttons'))) ||
            (t.closest && t.closest('.price, .product-form__buttons'))) {
          scheduleReapply();
          return;
        }

        // Newly added nodes that are or contain a relevant element
        for (var j = 0; j < m.addedNodes.length; j++) {
          var n = m.addedNodes[j];
          if (!n || n.nodeType !== 1) continue;
          var nClass = n.classList;
          if ((nClass && (nClass.contains('price') || nClass.contains('product-form__buttons'))) ||
              (n.querySelector && n.querySelector('.price, .product-form__buttons'))) {
            scheduleReapply();
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // First pass — observer set up first so any concurrent external write
  // is captured even if it lands between applyPriceGate and applyBuyGate.
  setupObserver();
  applyPriceGate();
  applyBuyGate();

  // Re-apply after variant change on product page
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, function () {
      setTimeout(resetAndReapply, 0);
    });
  }
})();
